import { useQueryClient } from "@tanstack/react-query";
import { useClosingRetrieveSuspense } from "gen/endpoints/closing/closing";
import { useFundFamilyEventRetrieve } from "gen/endpoints/fund-family-event/fund-family-event";
import { noop } from "lodash-es";
import React, { useCallback, useMemo } from "react";
import { Outlet } from "react-router";
import { FullScreenLoadingState } from "shared/components/empty/FullScreenLoadingState";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";

export const WrapperContext = React.createContext<{
  disablePolling: () => void;
}>({
  disablePolling: noop,
});

export const ClosingPageWrapper: React.FC = () => {
  const [shouldPoll, setShouldPoll] = React.useState(true);
  const { fundFamilyId, closingId } = useRequiredStringParams();
  const { data: closingData } = useClosingRetrieveSuspense(
    fundFamilyId,
    closingId,
  );

  const { data: fundFamilyEventData, queryKey } = useFundFamilyEventRetrieve(
    fundFamilyId,
    closingData.fund_family_event_id,
    {
      query: {
        enabled: shouldPoll,
        refetchInterval: 1000,
      },
    },
  );
  const queryClient = useQueryClient();

  const disablePolling = useCallback(() => {
    setShouldPoll(false);
    void queryClient.cancelQueries({
      queryKey,
    });
  }, [queryClient, queryKey]);

  const contextValue = useMemo(() => ({ disablePolling }), [disablePolling]);

  if (fundFamilyEventData?.event_workflow_status.is_running) {
    return (
      <PageContent isFullHeight={true}>
        <PageBody>
          <FullScreenLoadingState
            message={fundFamilyEventData.event_workflow_status.message}
          />
        </PageBody>
      </PageContent>
    );
  }

  return (
    <WrapperContext.Provider value={contextValue}>
      <Outlet />
    </WrapperContext.Provider>
  );
};
